// ============================================================
// TMR Portal Admin — as colunas da planilha do log de auditoria
// ============================================================
import { changedFields, formatAuditValue } from '../../data/authorization/audit';
import { auditActionLabel, auditEntityLabel, auditFieldLabel } from '../../data/authorization/labels';
import type { AuditLogEntry } from '../../data/authorization/contract';
import type { ExportColumn } from '../../lib/export/types';

/**
 * The authorization audit log as a spreadsheet (20 §9).
 *
 * The two snapshots do **not** go out as raw JSON. They are the whole point of the log and unreadable in
 * a cell — so what is exported is the same reading the drawer gives: which fields changed, and from what
 * to what, in the operator's words. A column of `{"name":"Administrador",…}` would be there for nobody.
 *
 * The actor is exported as an id: the screen resolves the name one row at a time, and a file of thousands
 * cannot. Registered in `20` §12 with the same gap on Contratos.
 */
export const AUDIT_LOG_EXPORT_COLUMNS: ExportColumn<AuditLogEntry>[] = [
  { header: 'Quando', value: (e) => new Date(e.at) },
  { header: 'Tipo', value: (e) => auditEntityLabel(e.entity) },
  { header: 'Ação', value: (e) => auditActionLabel(e.action) },
  { header: 'Registro', value: (e) => e.entityId ?? '' },
  // Null means the platform wrote it outside a session — the bootstrap, a migration — and "Sistema" is
  // what the screen says rather than leaving the cell to read as an unknown person.
  { header: 'Autor (id)', value: (e) => e.actorUserId ?? 'Sistema' },
  { header: 'Campos alterados', value: (e) => changeSummary(e) },
];

/** The change, read the way the drawer reads it: `Campo: de → para`, one per line. */
function changeSummary(entry: AuditLogEntry): string {
  return changedFields(entry.before, entry.after)
    .map(
      (change) =>
        `${auditFieldLabel(change.field)}: ${formatAuditValue(change.before)} → ${formatAuditValue(change.after)}`,
    )
    .join('\n');
}
